import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

const API_BASE_URL = '/auth';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Nettoie tout le localStorage auth
  const clearSession = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('session_expires');
    localStorage.removeItem('current_event');
  };

  // Charger et valider la session au démarrage
  useEffect(() => {
    const token          = localStorage.getItem('user_token');
    const savedUser      = localStorage.getItem('user_data');
    const sessionExpires = localStorage.getItem('session_expires');

    // Cas 1 : ancien token 'temp_token' ou données manquantes → déconnexion
    if (!token || token === 'temp_token' || !savedUser) {
      clearSession();
      setLoading(false);
      return;
    }

    // Cas 2 : pas de date d'expiry (ancienne session) ou token expiré → déconnexion
    if (!sessionExpires || new Date(sessionExpires) < new Date()) {
      clearSession();
      setLoading(false);
      return;
    }

    // Cas 3 : token valide en apparence → charger l'user et valider en arrière-plan
    try {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      setLoading(false);

      // Validation serveur silencieuse (non-bloquante)
      fetch('/auth/validate-session.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email: parsedUser?.email }),
      })
        .then(r => r.json())
        .then(data => {
          if (!data?.valid) {
            console.warn('[AuthContext] Session invalidée par le serveur');
            clearSession();
            setUser(null);
          }
        })
        .catch(() => {
          // Silencieux : si le serveur est injoignable, on garde la session locale
        });
    } catch (e) {
      console.error('Error parsing saved user:', e);
      clearSession();
      setLoading(false);
    }
  }, []);

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/register.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const sendCode = async (email, eventId, eventName, options = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}/send_code.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, event_id: eventId, event_name: eventName, ...options })
      });

      const data = await response.json();
      if (!response.ok || data.not_found) {
        const err = new Error(data.error || "Impossible d'envoyer le code.");
        err.not_found = !!data.not_found;
        throw err;
      }
      return data;
    } catch (error) {
      console.error('Send code error:', error);
      throw error;
    }
  };

  const verifyCode = async (email, eventId, code) => {
    try {
      const response = await fetch(`${API_BASE_URL}/verify_code.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, event_id: eventId, code })
      });

      const data = await response.json();

      // Lever une erreur explicite si la réponse HTTP indique un échec
      if (!response.ok) {
        throw new Error(data?.error || 'Code invalide. Réessayez.');
      }

      if (data.ok && data.user) {
        // Sauvegarder l'utilisateur et le token
        setUser(data.user);
        localStorage.setItem('user_token', data.session_token);
        localStorage.setItem('session_expires', data.session_expires);
        localStorage.setItem('user_data', JSON.stringify(data.user));

        // Sauvegarder l'event depuis les données utilisateur
        const userEvents = data.user?.events;
        if (userEvents && userEvents.length > 0) {
          const selectedEvent = {
            id: userEvents[0].event_id,
            name: userEvents[0].event_name,
            slug: userEvents[0].event_slug || null,
            subevent_id: userEvents[0].event_id
          };
          localStorage.setItem('current_event', JSON.stringify(selectedEvent));
        }
      }

      return data;
    } catch (error) {
      console.error('Verify code error:', error);
      throw error;
    }
  };

  // Mettre à jour le profil utilisateur dans le state et localStorage
  // Utilisé pour synchroniser le header avec les données Airtable après chargement du profil
  const updateUser = (partialData) => {
    setUser(prev => {
      const updated = { ...prev, ...partialData };
      localStorage.setItem('user_data', JSON.stringify(updated));
      return updated;
    });
  };

  const logout = () => {
    setUser(null);
    clearSession();
  };

  const isAuthenticated = () => {
    return !!localStorage.getItem('user_token');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        register,
        sendCode,
        verifyCode,
        logout,
        updateUser,
        isAuthenticated
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};